/**
 * USE DONATIONS HOOK
 * 
 * Manages donation data fetching, state, and mutations.
 * 
 * @module Donations/Hooks/useDonations
 */

import { useState, useEffect, useCallback } from 'react';
import { Donation, CreateDonationDTO, DonationStats } from '../types/donation.types';
import { donationService } from '../services/donationService';

interface UseDonationsReturn {
    donations: Donation[];
    loading: boolean;
    error: string | null;
    stats: DonationStats | null;
    loadDonations: () => Promise<void>;
    createDonation: (data: CreateDonationDTO) => Promise<Donation>;
    updateDonation: (id: string, data: Partial<Donation>) => Promise<Donation>;
    deleteDonation: (id: string) => Promise<void>;
    refreshStats: () => Promise<void>;
}

export const useDonations = (): UseDonationsReturn => {
    const [donations, setDonations] = useState<Donation[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [stats, setStats] = useState<DonationStats | null>(null);

    /**
     * Load all donations
     */
    const loadDonations = useCallback(async (): Promise<void> => {
        setLoading(true);
        setError(null);
        try {
            const data = await donationService.getDonations();
            setDonations(data);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to load donations';
            setError(message);
            setDonations([]);
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Refresh stats only
     */
    const refreshStats = useCallback(async (): Promise<void> => {
        try {
            const data = await donationService.getDonationStats();
            setStats(data);
        } catch (err) {
            console.error('Failed to load stats:', err);
        }
    }, []);

    /**
     * Create a new donation
     */
    const createDonation = useCallback(async (data: CreateDonationDTO): Promise<Donation> => {
        try {
            const newDonation = await donationService.createDonation(data);
            setDonations(prev => [newDonation, ...prev]);
            await refreshStats();
            return newDonation;
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to create donation';
            throw new Error(message);
        }
    }, [refreshStats]);

    /**
     * Update an existing donation
     */
    const updateDonation = useCallback(async (id: string, data: Partial<Donation>): Promise<Donation> => {
        try {
            const updated = await donationService.updateDonation(id, data);
            setDonations(prev => 
                prev.map(d => d._id === id ? updated : d)
            );
            await refreshStats();
            return updated;
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to update donation';
            throw new Error(message);
        }
    }, [refreshStats]);

    /**
     * Delete a donation
     */
    const deleteDonation = useCallback(async (id: string): Promise<void> => {
        try {
            await donationService.deleteDonation(id);
            setDonations(prev => prev.filter(d => d._id !== id));
            await refreshStats();
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to delete donation';
            throw new Error(message);
        }
    }, [refreshStats]);

    // Load donations and stats on mount
    useEffect(() => {
        loadDonations();
        refreshStats();
    }, [loadDonations, refreshStats]);

    return {
        donations,
        loading,
        error,
        stats,
        loadDonations,
        createDonation,
        updateDonation,
        deleteDonation,
        refreshStats
    };
};
